import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { useT } from "../lib/i18n";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

interface SignupScreenProps {
  onSwitchToLogin(): void;
}

export function SignupScreen({
  onSwitchToLogin,
}: SignupScreenProps): JSX.Element {
  const [, T] = useT();
  const { signup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const next = new URLSearchParams(location.search).get("next") ?? "/today";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signup({ email, password });
      void navigate(next, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : T("somethingWentWrong"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <span>{T("alreadyHaveAccount")}</span>
      <button type="button" className="vz-link" onClick={onSwitchToLogin}>
        {T("signIn")}
      </button>
    </>
  );

  return (
    <AuthShell title={T("signUp")} footer={footer} ariaLabel={T("signUp")}>
      <form
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div className="vz-auth-field">
          <label htmlFor="signup-email">{T("email")}</label>
          <input
            id="signup-email"
            className="vz-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="vz-auth-field">
          <label htmlFor="signup-password">{T("password")}</label>
          <input
            id="signup-password"
            className="vz-input"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--fg-3)",
            }}
          >
            {T("auth.passwordHint")}
          </span>
        </div>
        {error !== null && (
          <div className="vz-error" role="alert">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="vz-btn vz-btn-primary"
          disabled={submitting}
          style={{ justifyContent: "center", padding: "10px" }}
        >
          {submitting ? T("loading") : T("signUp")}
        </button>
      </form>
    </AuthShell>
  );
}
